<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, fetchStudyProperties } from '$stores/configStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue } from '$utils/unitUtils';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { exportSensorStatisticsToExcel, generateExportFilename } from '$utils/export.js';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faFilter,
    faCircleXmark
  } from '@fortawesome/pro-light-svg-icons';
  const browser = typeof window !== 'undefined';

  const API_BASE_URL = '/api/proxy';

  let statsData = [];
  let filteredData = [];
  let resultCount = 0;
  let isLoading = true;
  let isExporting = false;
  let hasError = false;
  let errorMessage = '';

  // Progress tracking for multi-site queries
  let loadingProgress = 0;
  let loadingTotal = 0;
  let loadingCurrentSite = '';

  // Pagination
  let currentPage = 1;
  let totalPages = 1;
  let limit = 50;

  // Filter state - default to TAR (combined) >= 40%
  let filterMetric = 'tarCombined';
  let filterOperator = '>=';
  let filterValue = '40';
  let isFilterActive = true;

  let currentFetch = null;
  let isInitialized = false;
  let isCancelled = false;

  // Get thresholds from study properties with defaults
  $: thresholds = $studyProperties?.glucoseParameters?.thresholds || {
    veryLow: 54,
    low: 70,
    high: 180,
    veryHigh: 250
  };

  // Helper to format threshold value based on current unit
  function formatThreshold(value, unit) {
    return convertGlucoseValue(value, unit);
  }

  // Metric options for the dropdown - derived from study thresholds and current unit
  // Explicitly depend on $glucoseunit to trigger re-render on unit change
  $: currentUnit = $glucoseunit;
  $: metricOptions = [
    { value: 'target', label: `TIR (Time in Range ${formatThreshold(thresholds.low, currentUnit)}-${formatThreshold(thresholds.high, currentUnit)})` },
    { value: 'tarCombined', label: `TAR (All Above Range >${formatThreshold(thresholds.high, currentUnit)})` },
    { value: 'high', label: `TAR1 (High ${formatThreshold(thresholds.high + 1, currentUnit)}-${formatThreshold(thresholds.veryHigh, currentUnit)})` },
    { value: 'veryHigh', label: `TAR2 (Very High >${formatThreshold(thresholds.veryHigh, currentUnit)})` },
    { value: 'tbrCombined', label: `TBR (All Below Range <${formatThreshold(thresholds.low, currentUnit)})` },
    { value: 'low', label: `TBR1 (Low ${formatThreshold(thresholds.veryLow, currentUnit)}-${formatThreshold(thresholds.low - 1, currentUnit)})` },
    { value: 'veryLow', label: `TBR2 (Very Low <${formatThreshold(thresholds.veryLow, currentUnit)})` },
    { value: 'completenessPercent', label: 'Completeness %' },
    { value: 'cvPercent', label: 'CV%' }
  ];

  // Dynamic description for FeatureInfo - uses study thresholds and current unit
  $: featureDescription = `This report shows CGM statistics for the selected site's participants, aggregated by sensor.
  Use the metric filter to find sensors that meet specific criteria.
  Default filter: TAR >= 40% (sensors with high time above range).
  
  *Metrics*
  *TIR* (Time in Range): % of readings ${formatThreshold(thresholds.low, currentUnit)}-${formatThreshold(thresholds.high, currentUnit)} ${currentUnit}
  *TAR* (Time Above Range): Combined % of all readings >${formatThreshold(thresholds.high, currentUnit)} ${currentUnit} (TAR1 + TAR2)
  *TAR1*: % of readings ${formatThreshold(thresholds.high + 1, currentUnit)}-${formatThreshold(thresholds.veryHigh, currentUnit)} ${currentUnit}
  *TAR2*: % of readings >${formatThreshold(thresholds.veryHigh, currentUnit)} ${currentUnit}
  *TBR* (Time Below Range): Combined % of all readings <${formatThreshold(thresholds.low, currentUnit)} ${currentUnit} (TBR1 + TBR2)
  *TBR1*: % of readings ${formatThreshold(thresholds.veryLow, currentUnit)}-${formatThreshold(thresholds.low - 1, currentUnit)} ${currentUnit}
  *TBR2*: % of readings <${formatThreshold(thresholds.veryLow, currentUnit)} ${currentUnit}
  `;

  // Subscribe to selectedStudy changes to fetch properties
  $: if ($selectedStudy?._id) {
    fetchStudyProperties($selectedStudy._id);
  } else {
    studyProperties.set(null);
  }

  // Add cleanup on component destroy
  onDestroy(() => {
    if (currentFetch) {
      currentFetch.abort();
    }
  });

  // Add a store subscription, but only in the browser
  // Requires study to be selected, site is optional (for testing performance)
  $: if (browser && isInitialized && $selectedStudy) {
    fetchStudyStats().catch(err => {
      console.error('Error in store subscription:', err);
      hasError = true;
      errorMessage = err.message;
    });
  }

  onMount(async () => {
    if (!browser) return;

    try {
      selectedParticipant.set(null);

      if (!$selectedStudy) {
        goto('/studies');
        return;
      }

      // Mark as initialized after initial setup
      isInitialized = true;
    } catch (err) {
      console.error('Error in onMount:', err);
      hasError = true;
      errorMessage = err.message;
    }
  });

  // Create an abort controller factory
  function createAbortController() {
    if (currentFetch) {
      currentFetch.abort();
    }
    return new AbortController();
  }

  // Fetch stats for a single site
  async function fetchSiteStats(siteId, siteGUID, abortController) {
    const url = `${API_BASE_URL}/stats/studyCGMStats?page=1&pageSize=999999&studyGUID=${$selectedStudy._id}&siteGUID=${siteGUID}&inRange=true`;
    
    const res = await fetch(url, {
      signal: abortController.signal
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(`Failed to fetch stats for site ${siteId}: ${res.status} ${errorData.message || ''}`);
    }

    const responseData = await res.json();
    return (responseData.data || []).map(stat => ({
      ...stat,
      tarCombined: stat.tarCombined ?? ((stat.high?.percent || 0) + (stat.veryHigh?.percent || 0)),
      tbrCombined: stat.tbrCombined ?? ((stat.low?.percent || 0) + (stat.veryLow?.percent || 0))
    }));
  }

  // Fetch all sites for the study
  async function fetchStudySites() {
    const res = await fetch(`${API_BASE_URL}/sites?studyGUID=${$selectedStudy._id}&page=1&pageSize=1000&sortOrder=asc`);
    if (!res.ok) {
      throw new Error('Failed to fetch sites');
    }
    const data = await res.json();
    return data.data || [];
  }

  async function fetchStudyStats() {
    const abortController = createAbortController();
    currentFetch = abortController;
    isCancelled = false;

    if (!$selectedStudy || !$selectedStudy._id) {
      statsData = [];
      filteredData = [];
      resultCount = 0;
      return;
    }

    if (!API_BASE_URL) {
      console.error('Missing API configuration');
      return;
    }

    try {
      isLoading = true;
      hasError = false;
      errorMessage = '';
      loadingProgress = 0;
      loadingTotal = 0;
      loadingCurrentSite = '';
      
      // If a specific site is selected, use single query
      if ($selectedSite?._id) {
        const siteData = await fetchSiteStats($selectedSite.siteId, $selectedSite._id, abortController);
        if (currentFetch === abortController && !isCancelled) {
          statsData = siteData;
          applyFilter();
        }
      } else {
        // No site selected - fetch all sites sequentially
        const sites = await fetchStudySites();
        loadingTotal = sites.length;
        
        if (sites.length === 0) {
          statsData = [];
          applyFilter();
          return;
        }

        // Process sites sequentially and combine results
        let allData = [];
        
        for (let i = 0; i < sites.length; i++) {
          // Check if cancelled
          if (isCancelled || currentFetch !== abortController) {
            return;
          }
          
          const site = sites[i];
          loadingProgress = i + 1;
          loadingCurrentSite = site.siteId || `Site ${i + 1}`;
          
          try {
            const siteData = await fetchSiteStats(site.siteId, site._id, abortController);
            allData = [...allData, ...siteData];
            
            // Update UI incrementally so user sees progress
            if (currentFetch === abortController && !isCancelled) {
              statsData = allData;
              applyFilter();
            }
          } catch (siteErr) {
            // Log error but continue with other sites
            console.error(`Error fetching site ${site.siteId}:`, siteErr);
          }
        }
        
        loadingCurrentSite = '';
      }

    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching study CGM stats:', err);
      statsData = [];
      filteredData = [];
      resultCount = 0;
      hasError = true;
      errorMessage = err.message || 'Failed to load study CGM statistics. Make sure the server is running with the latest code.';
    } finally {
      if (currentFetch === abortController) {
        isLoading = false;
        currentFetch = null;
        loadingProgress = 0;
        loadingTotal = 0;
        loadingCurrentSite = '';
      }
    }
  }

  // Cancel ongoing multi-site fetch
  function cancelFetch() {
    isCancelled = true;
    if (currentFetch) {
      currentFetch.abort();
    }
  }

  function applyFilter() {
    if (!isFilterActive || filterValue === '') {
      filteredData = [...statsData];
    } else {
      const threshold = parseFloat(filterValue);
      if (isNaN(threshold)) {
        filteredData = [...statsData];
      } else {
        filteredData = statsData.filter(stat => {
          let value;
          // Direct properties (not nested under .percent)
          if (filterMetric === 'completenessPercent' || filterMetric === 'cvPercent' || 
              filterMetric === 'tarCombined' || filterMetric === 'tbrCombined') {
            value = stat[filterMetric];
          } else {
            // Nested properties under .percent (veryLow, low, target, high, veryHigh)
            value = stat[filterMetric]?.percent;
          }

          if (value === undefined || value === null) return false;

          switch (filterOperator) {
            case '>=':
              return value >= threshold;
            case '<=':
              return value <= threshold;
            case '=':
              return value === threshold;
            case '>':
              return value > threshold;
            case '<':
              return value < threshold;
            default:
              return true;
          }
        });
      }
    }

    resultCount = filteredData.length;
    totalPages = Math.ceil(resultCount / limit);
    currentPage = 1;
  }

  function handleApplyFilter() {
    if (filterValue !== '') {
      isFilterActive = true;
    }
    applyFilter();
  }

  function handleClearFilter() {
    filterValue = '';
    isFilterActive = false;
    applyFilter();
  }

  function handleRefresh() {
    fetchStudyStats();
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      currentPage = newPage;
    }
  }

  function handleParticipantClick(item) {
    if (item.siteGUID || item._id?.subjectGUID) {
      // Set the site context if available
      if (item.siteId) {
        const site = {
          _id: item.siteGUID,
          siteId: item.siteId,
          country: item.country
        };
        selectedSite.set(site);
      }
      selectedParticipant.set({ _id: item._id?.subjectGUID, subjectId: item.subjectId });
      goto(`/person`);
    }
  }

  async function handleExportExcel() {
    try {
      isExporting = true;

      if (!$selectedStudy || !$selectedStudy._id) {
        throw new Error('No study selected');
      }

      // Export the currently filtered data
      const dataToExport = filteredData;

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        siteId: $selectedSite?.siteId,
        reportType: 'CGM_Sensor_Statistics'
      });

      const formattedData = dataToExport.map((stat) => ({
        'Protocol': $selectedStudy?.protocolNumber || '',
        'Site ID': stat.siteId || '',
        'Country': stat.country || '',
        'Subject ID': stat.subjectId || '',
        'Status': stat.statusLabel || '',
        'Sensor': stat._id?.externalId || '',
        'Readings': stat.total || 0,
        'Completeness (%)': stat.completenessPercent || 0,
        'Mean': convertGlucoseValue(stat.mean, $glucoseunit)?.toFixed(1) || 'N/A',
        'Unit': $glucoseunit,
        'GMI (%)': stat.gmi || 0,
        'SD': convertGlucoseValue(stat.sd, $glucoseunit)?.toFixed(1) || 'N/A',
        'CV (%)': stat.cvPercent || 0,
        'TBR2 (%)': stat.veryLow?.percent || 0,
        'TBR2 Count': stat.veryLow?.count || 0,
        'TBR1 (%)': stat.low?.percent || 0,
        'TBR1 Count': stat.low?.count || 0,
        'TBR (%)': stat.tbrCombined || 0,
        'TIR (%)': stat.target?.percent || 0,
        'TIR Count': stat.target?.count || 0,
        'TAR (%)': stat.tarCombined || 0,
        'TAR1 (%)': stat.high?.percent || 0,
        'TAR1 Count': stat.high?.count || 0,
        'TAR2 (%)': stat.veryHigh?.percent || 0,
        'TAR2 Count': stat.veryHigh?.count || 0,
        'First Reading': formatToLocalDateTime(stat.firstRead, stat.timezone),
        'Last Reading': formatToLocalDateTime(stat.lastRead, stat.timezone),
        'Timezone': stat.timezone || ''
      }));

      await exportSensorStatisticsToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }

  // Get paginated data for display
  $: paginatedData = filteredData.slice((currentPage - 1) * limit, currentPage * limit);
</script>

{#if hasError}
  <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg mb-4">
    <p>An error occurred: {errorMessage}</p>
    <button
      class="mt-2 px-4 py-2 bg-red-200 dark:bg-red-800 rounded hover:bg-red-300 dark:hover:bg-red-700"
      on:click={() => {
        hasError = false;
        errorMessage = '';
        if ($selectedStudy) {
          fetchStudyStats();
        }
      }}
    >
      Try Again
    </button>
  </div>
{/if}

<StudyHeader />

<FeatureInfo
  title="CGM Sensor Statistics Report"
  description={featureDescription}
  marginBottom="mb-0"
/>

{#if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<Toolbar title="CGM Sensor Statistics - {$selectedSite?.siteId ? `Site ${$selectedSite.siteId}` : 'All Sites'}" itemCountTitle="Sensors" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <!-- Metric Filter Controls -->
    <div class="flex items-center gap-2">
      <span class="text-xs text-gray-600 dark:text-gray-400">Filter:</span>
      <select
        bind:value={filterMetric}
        class="form-select h-6 border px-2 p-0 rounded text-xs dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white w-48"
      >
        {#each metricOptions as option}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
      <select
        bind:value={filterOperator}
        class="form-select h-6 border px-2 p-0 rounded text-xs dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none w-14"
      >
        <option value=">=">&gt;=</option>
        <option value="<=">&lt;=</option>
        <option value="=">=</option>
        <option value=">">&gt;</option>
        <option value="<">&lt;</option>
      </select>
      <div class="relative">
        <input
          type="number"
          placeholder="%"
          bind:value={filterValue}
          on:keydown={(e) => e.key === 'Enter' && handleApplyFilter()}
          class="w-16 px-2 py-1 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          min="0"
          max="100"
          step="0.1"
        />
      </div>
      <button
        on:click={handleApplyFilter}
        class="px-2 py-1 text-xs bg-sky-600 hover:bg-sky-700 text-white rounded"
        title="Apply filter"
      >
        Apply
      </button>
      {#if isFilterActive}
        <button
          on:click={handleClearFilter}
          class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Clear filter"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      {/if}
    </div>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725">
      <div class="loader mb-4"></div>
      {#if loadingTotal > 0}
        <p class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Loading site {loadingProgress} of {loadingTotal}
          {#if loadingCurrentSite}
            <span class="text-gray-500 dark:text-gray-400">({loadingCurrentSite})</span>
          {/if}
        </p>
        <div class="w-64 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-2">
          <div 
            class="h-full bg-blue-500 transition-all duration-300"
            style="width: {(loadingProgress / loadingTotal) * 100}%"
          ></div>
        </div>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          {statsData.length} sensors loaded so far
        </p>
        <button
          class="mt-3 px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
          on:click={cancelFetch}
        >
          Cancel
        </button>
      {:else}
        <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
          Loading CGM statistics...
        </p>
      {/if}
    </div>
  {:else if paginatedData.length > 0}
    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse table-auto">
        <thead>
          <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
            <th class="px-3 py-2 text-left bold sticky left-0 z-20 bg-stone-200 dark:bg-gray-800 min-w-[60px]">Site</th>
            <th class="px-3 py-2 text-left bold sticky left-[60px] z-20 bg-stone-200 dark:bg-gray-800 min-w-[80px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]">Subject</th>
            <th class="px-3 py-2 text-left bold min-w-[160px]">Sensor</th>
            <th class="px-3 py-2 text-left bold">Status</th>
            <th class="px-3 py-2 text-left bold">Readings</th>
            <th class="px-3 py-2 text-left bold">Comp.</th>
            <th class="px-3 py-2 text-left bold">Mean</th>
            <th class="px-3 py-2 text-left bold">GMI</th>
            <th class="px-3 py-2 text-left bold">SD</th>
            <th class="px-3 py-2 text-left bold">CV%</th>
            <th class="px-3 py-2 text-left bold">TBR2</th>
            <th class="px-3 py-2 text-left bold">TBR1</th>
            <th class="px-3 py-2 text-left bold bg-amber-100 dark:bg-amber-900/30">TBR</th>
            <th class="px-3 py-2 text-left bold bg-green-100 dark:bg-green-900/30">TIR</th>
            <th class="px-3 py-2 text-left bold bg-red-100 dark:bg-red-900/30">TAR</th>
            <th class="px-3 py-2 text-left bold">TAR1</th>
            <th class="px-3 py-2 text-left bold">TAR2</th>
            <th class="px-3 py-2 text-left bold whitespace-nowrap">First (Local)</th>
            <th class="px-3 py-2 text-left bold whitespace-nowrap">Last (Local)</th>
          </tr>
        </thead>
        <tbody>
          {#each paginatedData as stat, index}
            <tr
              class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
                2 ===
              0
                ? 'bg-stone-50 dark:bg-gray-725'
                : 'bg-stone-75 dark:bg-gray-750'}"
            >
              <td class="px-3 py-2 sticky left-0 z-20 min-w-[60px] {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">{stat.siteId || ''}</td>
              <td 
                class="px-3 py-2 whitespace-nowrap sticky left-[60px] z-20 min-w-[80px] cursor-pointer hover:underline {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]" 
                on:click={() => handleParticipantClick(stat)}
              >
                {stat.subjectId || ''}
              </td>
              <td class="px-3 py-2 whitespace-nowrap min-w-[160px]">{stat._id?.externalId || ''}</td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                  class:text-green-600={stat.statusLabel === 'Enrolled' || stat.statusLabel === 'Screened/Enrolled'}
                  class:ring-green-600={stat.statusLabel === 'Enrolled' || stat.statusLabel === 'Screened/Enrolled'}
                  class:text-gray-600={stat.statusLabel === 'Discontinued'}
                  class:ring-gray-600={stat.statusLabel === 'Discontinued'}
                  class:text-sky-600={stat.statusLabel === 'Completed'}
                  class:ring-sky-600={stat.statusLabel === 'Completed'}
                >
                  {stat.statusLabel || ''}
                </span>
              </td>
              <td class="px-3 py-2">{stat.total || 0}</td>
              <td class="px-3 py-2">{stat.completenessPercent || 0}%</td>
              <td class="px-3 py-2 whitespace-nowrap">{convertGlucoseValue(stat.mean, $glucoseunit)?.toFixed(1) || 'N/A'} {$glucoseunit}</td>
              <td class="px-3 py-2 whitespace-nowrap">{stat.gmi !== null && stat.gmi !== undefined ? stat.gmi : 'N/A'}%</td>
              <td class="px-3 py-2 whitespace-nowrap">{convertGlucoseValue(stat.sd, $glucoseunit)?.toFixed(1) || 'N/A'} {$glucoseunit}</td>
              <td class="px-3 py-2">{stat.cvPercent !== null && stat.cvPercent !== undefined ? stat.cvPercent : 'N/A'}%</td>
              <td class="px-3 py-2">{stat.veryLow?.percent || 0}%</td>
              <td class="px-3 py-2">{stat.low?.percent || 0}%</td>
              <td class="px-3 py-2 bg-amber-50 dark:bg-amber-900/20">{stat.tbrCombined || 0}%</td>
              <td class="px-3 py-2 bg-green-50 dark:bg-green-900/20 font-medium">{stat.target?.percent || 0}%</td>
              <td class="px-3 py-2 bg-red-50 dark:bg-red-900/20 font-medium">{stat.tarCombined || 0}%</td>
              <td class="px-3 py-2">{stat.high?.percent || 0}%</td>
              <td class="px-3 py-2">{stat.veryHigh?.percent || 0}%</td>
              <td class="px-3 py-2 whitespace-nowrap text-xs">{formatToLocalDateTime(stat.firstRead, stat.timezone)}</td>
              <td class="px-3 py-2 whitespace-nowrap text-xs">{formatToLocalDateTime(stat.lastRead, stat.timezone)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      {#if isFilterActive}
        {@const metricLabel = metricOptions.find(m => m.value === filterMetric)?.label || filterMetric}
        No participants match the filter: {metricLabel} {filterOperator} {filterValue}%
      {:else}
        No CGM data found for this {$selectedSite ? 'site' : 'study'}
      {/if}
    </div>
  {/if}
  <!-- {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      {#if !$selectedSite}
        Please select a site to view sensor statistics
      {:else if isFilterActive}
        {@const metricLabel = metricOptions.find(m => m.value === filterMetric)?.label || filterMetric}
        No sensors match the filter: {metricLabel} {filterOperator} {filterValue}%
      {:else}
        No CGM data found for this site
      {/if}
    </div>
  {/if} -->
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  /* Hide scrollbar for Chrome, Safari and Opera */
  .overflow-x-auto::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  .overflow-x-auto {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>

